import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  LineChart as RechartsLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { parentApi } from '../../lib/parentApi';
import { SkillGrid } from '../../components/SkillGrid';
import { useSkillmapGrid } from '../../hooks/useSkillmapGrid';
import { Button, ListToolbar, Tile, TileContent, TileHeader, TileTitle } from '../../components/ui';

function toDateKeyLocal(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function currentSchoolYearRange() {
  const now = new Date();
  const start = now.getMonth() + 1 >= 8 ? new Date(now.getFullYear(), 7, 1) : new Date(now.getFullYear() - 1, 7, 1);
  return { startDate: toDateKeyLocal(start), endDate: toDateKeyLocal(now) };
}

export default function ParentProgress() {
  const [selectedUnid, setSelectedUnid] = useState('');
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'in_progress' | 'competent' | 'proficient' | 'not_yet_competent' | 'not_attempted'>('all');
  const [collapsedCurricula, setCollapsedCurricula] = useState<Set<number>>(new Set());

  const { data: children = [] } = useQuery(['parent-children'], () => parentApi.children());
  useEffect(() => {
    if (!selectedUnid && children.length > 0) {
      setSelectedUnid(children[0].sophomorixUnid);
    }
  }, [children, selectedUnid]);

  const { data: dashboard } = useQuery(
    ['parent-dashboard', selectedUnid],
    () => parentApi.dashboard(selectedUnid),
    { enabled: Boolean(selectedUnid) }
  );
  const { data: skillmap } = useQuery(
    ['parent-skillmap', selectedUnid],
    () => parentApi.skillmap(selectedUnid),
    { enabled: Boolean(selectedUnid) }
  );
  const { data: evidenceTrend } = useQuery(
    ['parent-evidence-trend', selectedUnid],
    () => {
      const { startDate, endDate } = currentSchoolYearRange();
      return parentApi.evidenceTrend(selectedUnid, {
        subject: 'ALL',
        timeScope: 'custom',
        startDate,
        endDate,
        includeClassAverage: 'true'
      });
    },
    { enabled: Boolean(selectedUnid) }
  );

  const { gridItems } = useSkillmapGrid({
    data: skillmap,
    filterStatus,
    collapsedCurricula
  });

  const selectedSkill = useMemo(() => {
    if (!skillmap || !selectedSkillId) return null;
    const id = selectedSkillId.replace('competency-', '');
    return skillmap.competencies.find((item: any) => String(item.id) === id) || null;
  }, [skillmap, selectedSkillId]);

  return (
    <div className="space-y-4">
      <ListToolbar
        title="Lernerfolge"
        description="Fortschritte und Skillmap pro Kind."
      />

      <Tile>
        <TileHeader>
          <TileTitle>Kind auswählen</TileTitle>
        </TileHeader>
        <TileContent>
          <select
            className="input-field min-h-[2.25rem] py-2 text-sm"
            value={selectedUnid}
            onChange={(event) => setSelectedUnid(event.target.value)}
          >
            {children.map((child) => (
              <option key={child.sophomorixUnid} value={child.sophomorixUnid}>
                {child.Vorname} {child.Name} ({child.Klasse})
              </option>
            ))}
          </select>
        </TileContent>
      </Tile>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Tile>
          <TileHeader>
            <TileTitle>Gesamtfortschritt</TileTitle>
          </TileHeader>
          <TileContent>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {dashboard?.progress?.Gesamt?.progress ?? 0}%
            </p>
          </TileContent>
        </Tile>
        <Tile>
          <TileHeader>
            <TileTitle>Erledigte Aufgaben</TileTitle>
          </TileHeader>
          <TileContent>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {dashboard?.progress?.Gesamt?.completedTasks ?? 0} / {dashboard?.progress?.Gesamt?.totalTasks ?? 0}
            </p>
          </TileContent>
        </Tile>
        <Tile>
          <TileHeader>
            <TileTitle>Skillmap-Kompetenzen</TileTitle>
          </TileHeader>
          <TileContent>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {skillmap?.summary?.totalCompetencies ?? 0}
            </p>
          </TileContent>
        </Tile>
      </div>

      <Tile>
        <TileHeader>
          <TileTitle>Belege-Verlauf</TileTitle>
        </TileHeader>
        <TileContent>
          <div className="h-72 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900">
            {evidenceTrend?.data?.timeline?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsLineChart data={evidenceTrend.data.timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(value) => new Date(String(value)).toLocaleDateString('de-DE')} />
                  <YAxis />
                  <Tooltip />
                  {evidenceTrend.data.series?.map((series: any) => (
                    <Line
                      key={series.key}
                      type="monotone"
                      dataKey={series.key}
                      stroke={series.type === 'classAverage' ? '#111827' : '#dc2626'}
                      dot={false}
                      strokeDasharray={series.type === 'classAverage' ? '5 5' : undefined}
                    />
                  ))}
                </RechartsLineChart>
              </ResponsiveContainer>
            ) : (
              <p className="p-4 text-sm text-gray-600 dark:text-gray-400">Keine Daten verfügbar.</p>
            )}
          </div>
        </TileContent>
      </Tile>

      <Tile>
        <TileHeader>
          <TileTitle>Skillmap</TileTitle>
        </TileHeader>
        <TileContent>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <select
              className="input-field min-h-[2.25rem] py-2 text-sm"
              value={filterStatus}
              onChange={(event) =>
                setFilterStatus(
                  event.target.value as 'all' | 'in_progress' | 'competent' | 'proficient' | 'not_yet_competent' | 'not_attempted'
                )
              }
            >
              <option value="all">Alle</option>
              <option value="in_progress">In Arbeit</option>
              <option value="competent">Kompetent</option>
              <option value="proficient">Proficient</option>
              <option value="not_yet_competent">Noch nicht sicher</option>
              <option value="not_attempted">Nicht gestartet</option>
            </select>
            {skillmap?.curricula?.map((curriculum: any) => {
              const collapsed = collapsedCurricula.has(curriculum.curriculumId);
              return (
                  <Button
                    key={curriculum.curriculumId}
                    type="button"
                    size="sm"
                    variant={collapsed ? 'outline' : 'primary'}
                    onClick={() => {
                      setCollapsedCurricula((current) => {
                        const next = new Set(current);
                        if (collapsed) next.delete(curriculum.curriculumId);
                        else next.add(curriculum.curriculumId);
                        return next;
                      });
                    }}
                  >
                    {curriculum.label || curriculum.subject || curriculum.name}
                    {collapsed ? ' (aus)' : ''}
                  </Button>
              );
            })}
          </div>
          {gridItems.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                <SkillGrid items={gridItems} onTileClick={(item) => setSelectedSkillId(item.id)} />
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                {selectedSkill ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {selectedSkill.childFriendlyName || selectedSkill.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Status: {selectedSkill.status}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Versuche: {selectedSkill.attemptCount}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">Kompetenz auswählen, um Details zu sehen.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">Keine Skillmap-Daten verfügbar.</p>
          )}
        </TileContent>
      </Tile>
    </div>
  );
}
